import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '../hooks/useToast';
import { useConfirm } from '../hooks/useConfirm';
import { renderMarkdown } from '../lib/renderMarkdown';
import { extractFullText } from '../lib/pdfTextExtractor';
import type { RegulatoryDocument, RegulatoryCategory, RegulatorySector } from '../types';
import {
  Search, Plus, Edit3, Trash2, X, Save, ExternalLink,
  BookOpen, Shield, Zap, ClipboardCheck, Leaf,
  Building2, Factory, ArrowLeft, MessageSquare, Loader2, Send,
  Upload, FileText, Check, Download, HelpCircle
} from 'lucide-react';

// ─── CONFIG CATÉGORIES & SECTEURS ────────────────────────────────────────────

const CATEGORY_CONFIG: Record<RegulatoryCategory, { label: string; color: string; bg: string; icon: typeof Shield }> = {
  normes:       { label: 'Normes',       color: '#2563eb', bg: '#eff6ff',  icon: BookOpen },
  securite:     { label: 'Sécurité',     color: '#dc2626', bg: '#fef2f2',  icon: Shield },
  installation: { label: 'Installation', color: '#059669', bg: '#ecfdf5',  icon: Zap },
  verification: { label: 'Vérification', color: '#d97706', bg: '#fffbeb',  icon: ClipboardCheck },
  energie:      { label: 'Énergie',      color: '#7c3aed', bg: '#f5f3ff',  icon: Leaf },
};

const SECTOR_CONFIG: Record<RegulatorySector, { label: string; color: string; bg: string; icon: typeof Building2 }> = {
  tertiaire:  { label: 'Tertiaire',     color: '#0ea5e9', bg: '#e0f2fe', icon: Building2 },
  industriel: { label: 'Industriel',    color: '#f97316', bg: '#fff7ed', icon: Factory },
  both:       { label: 'Tous secteurs', color: '#64748b', bg: '#f1f5f9', icon: Building2 },
};

// ─── TYPES LOCAUX ────────────────────────────────────────────────────────────

interface LlmSource {
  title: string;
  document_id: string;
  pages: number[];
  source_url: string | null;
}

// ─── COMPOSANT ───────────────────────────────────────────────────────────────

export default function ReglementationTab() {
  const { toast } = useToast();
  const { confirm: confirmDialog } = useConfirm();
  // Data
  const [documents, setDocuments] = useState<RegulatoryDocument[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [filterSector, setFilterSector] = useState<RegulatorySector | 'all'>('all');
  const [filterCategory, setFilterCategory] = useState<RegulatoryCategory | 'all'>('all');

  // Views
  const [selectedDoc, setSelectedDoc] = useState<RegulatoryDocument | null>(null);
  const [docContent, setDocContent] = useState<string>('');
  const [loadingContent, setLoadingContent] = useState(false);

  // CRUD modal
  const [showForm, setShowForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState<RegulatoryDocument | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formCategory, setFormCategory] = useState<RegulatoryCategory>('normes');
  const [formSector, setFormSector] = useState<RegulatorySector>('both');
  const [formSummary, setFormSummary] = useState('');
  const [formSourceUrl, setFormSourceUrl] = useState('');
  const [formFile, setFormFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');

  // LLM Q&A
  const [showQuestion, setShowQuestion] = useState(false);
  const [question, setQuestion] = useState('');
  const [llmAnswer, setLlmAnswer] = useState('');
  const [llmSources, setLlmSources] = useState<LlmSource[]>([]);
  const [llmLoading, setLlmLoading] = useState(false);

  // ─── FETCH DOCUMENTS ─────────────────────────────────────────────────────

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('regulatory_documents')
        .select('*')
        .order('category')
        .order('title');

      if (error) throw error;
      setDocuments((data || []) as RegulatoryDocument[]);
    } catch (err) {
      console.error('Erreur chargement documents:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  // ─── FILTRAGE ─────────────────────────────────────────────────────────────

  const filteredDocs = useMemo(() => {
    return documents.filter(d => {
      const matchSearch = searchText === '' ||
        d.title.toLowerCase().includes(searchText.toLowerCase()) ||
        (d.summary || '').toLowerCase().includes(searchText.toLowerCase());
      const matchSector = filterSector === 'all' || d.sector === filterSector || d.sector === 'both';
      const matchCategory = filterCategory === 'all' || d.category === filterCategory;
      return matchSearch && matchSector && matchCategory;
    });
  }, [documents, searchText, filterSector, filterCategory]);

  // ─── VOIR DÉTAIL ──────────────────────────────────────────────────────────

  const viewDocument = async (doc: RegulatoryDocument) => {
    setSelectedDoc(doc);
    setLoadingContent(true);
    try {
      // Charger les chunks du document pour reconstituer le contenu
      const { data, error } = await supabase
        .from('regulatory_chunks')
        .select('content, chunk_index, page_number')
        .eq('document_id', doc.id)
        .order('chunk_index');

      if (error) throw error;
      const fullText = (data || []).map(c => c.content).join('\n\n');
      setDocContent(fullText || 'Aucun contenu indexé pour ce document.');
    } catch (err) {
      console.error('Erreur chargement contenu:', err);
      setDocContent('Erreur lors du chargement du contenu.');
    } finally {
      setLoadingContent(false);
    }
  };

  // ─── CRUD ─────────────────────────────────────────────────────────────────

  const openCreateForm = () => {
    setEditingDoc(null);
    setFormTitle('');
    setFormCategory('normes');
    setFormSector('both');
    setFormSummary('');
    setFormSourceUrl('');
    setFormFile(null);
    setUploadProgress('');
    setShowForm(true);
  };

  const openEditForm = (doc: RegulatoryDocument) => {
    setEditingDoc(doc);
    setFormTitle(doc.title);
    setFormCategory(doc.category);
    setFormSector(doc.sector);
    setFormSummary(doc.summary || '');
    setFormSourceUrl(doc.source_url || '');
    setFormFile(null);
    setUploadProgress('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formTitle.trim()) return;
    setSaving(true);

    try {
      let filePath: string | null = editingDoc?.file_path || null;
      let fileName: string | null = editingDoc?.file_name || null;

      // 1. Upload PDF si fourni
      if (formFile) {
        setUploadProgress('Upload du PDF...');
        const fileExt = formFile.name.split('.').pop();
        const path = `regulatory/${Date.now()}_${formFile.name}`;
        const { error: uploadError } = await supabase.storage
          .from('regulatory-docs')
          .upload(path, formFile, { contentType: `application/${fileExt}` });

        if (uploadError) throw new Error(`Upload échoué: ${uploadError.message}`);
        filePath = path;
        fileName = formFile.name;
      }

      // 2. Insert ou Update le document
      const payload = {
        title: formTitle.trim(),
        category: formCategory,
        sector: formSector,
        summary: formSummary.trim() || null,
        source_url: formSourceUrl.trim() || null,
        file_path: filePath,
        file_name: fileName,
      };

      let documentId: string;

      if (editingDoc) {
        const { error } = await supabase
          .from('regulatory_documents')
          .update(payload)
          .eq('id', editingDoc.id);
        if (error) throw error;
        documentId = editingDoc.id;
      } else {
        const { data, error } = await supabase
          .from('regulatory_documents')
          .insert(payload)
          .select('id')
          .single();
        if (error) throw error;
        documentId = data.id;
      }

      // 3. Si nouveau PDF, extraire et indexer
      if (formFile) {
        setUploadProgress('Extraction du texte...');
        const { text, pageTexts } = await extractFullText(formFile);

        setUploadProgress('Indexation en cours...');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Non authentifié');

        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/regulatory-embed`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              document_id: documentId,
              text,
              page_texts: pageTexts,
            }),
          }
        );

        const result = await res.json();
        if (!res.ok) throw new Error(result.error || 'Erreur indexation');
        setUploadProgress(`✓ Indexé : ${result.chunk_count} segments`);
      }

      setShowForm(false);
      fetchDocuments();
      toast.success(editingDoc ? 'Document mis à jour.' : 'Document créé avec succès.');
      // Auto-clear du message de succès après 2.5s
      setTimeout(() => setUploadProgress(''), 2500);
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      toast.error(`Erreur : ${err instanceof Error ? err.message : 'Inconnue'}`);
      setUploadProgress(`Erreur : ${err instanceof Error ? err.message : 'Inconnue'}`);
      setTimeout(() => setUploadProgress(''), 4000);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (doc: RegulatoryDocument) => {
    const ok = await confirmDialog({ title: "Supprimer ce document ?", message: `Supprimer "${doc.title}" et tous ses chunks indexés ?`, variant: "danger" });
    if (!ok) return;

    try {
      // Supprimer le PDF du storage si présent
      if (doc.file_path) {
        await supabase.storage.from('regulatory-docs').remove([doc.file_path]);
      }
      // Les chunks sont supprimés en cascade (ON DELETE CASCADE)
      const { error } = await supabase
        .from('regulatory_documents')
        .delete()
        .eq('id', doc.id);
      if (error) throw error;
      fetchDocuments();
      if (selectedDoc?.id === doc.id) setSelectedDoc(null);
      toast.success('Document supprimé.');
    } catch (err) {
      console.error('Erreur suppression:', err);
      toast.error('Erreur lors de la suppression du document.');
    }
  };

  // ─── TÉLÉCHARGER PDF ──────────────────────────────────────────────────────

  const downloadPdf = async (doc: RegulatoryDocument) => {
    if (!doc.file_path) return;
    const { data } = await supabase.storage
      .from('regulatory-docs')
      .createSignedUrl(doc.file_path, 60 * 60);
    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank');
    }
  };

  // ─── RE-INDEXER ───────────────────────────────────────────────────────────

  const reindexDocument = async (doc: RegulatoryDocument) => {
    if (!doc.file_path) return;

    try {
      setUploadProgress('Téléchargement du PDF...');
      // Télécharger le PDF depuis le storage
      const { data: fileData, error: dlError } = await supabase.storage
        .from('regulatory-docs')
        .download(doc.file_path);
      if (dlError || !fileData) throw new Error('Impossible de télécharger le PDF');

      const file = new File([fileData], doc.file_name || 'document.pdf', { type: 'application/pdf' });
      setUploadProgress('Extraction du texte...');
      const { text, pageTexts } = await extractFullText(file);

      setUploadProgress('Indexation en cours...');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Non authentifié');

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/regulatory-embed`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ document_id: doc.id, text, page_texts: pageTexts }),
        }
      );

      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Erreur indexation');
      setUploadProgress('');
      fetchDocuments();
    } catch (err) {
      console.error('Erreur ré-indexation:', err);
      setUploadProgress(`Erreur : ${err instanceof Error ? err.message : 'Inconnue'}`);
      setTimeout(() => setUploadProgress(''), 3000);
    }
  };

  // ─── LLM QUESTION ─────────────────────────────────────────────────────────

  const askQuestion = async () => {
    if (!question.trim() || llmLoading) return;
    setLlmLoading(true);
    setLlmAnswer('');
    setLlmSources([]);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Non authentifié');

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/regulatory-query`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            question: question.trim(),
            sector: filterSector !== 'all' ? filterSector : undefined,
            category: filterCategory !== 'all' ? filterCategory : undefined,
          }),
        }
      );

      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Erreur');
      setLlmAnswer(result.response);
      setLlmSources(result.sources || []);
    } catch (err) {
      setLlmAnswer(`Erreur : ${err instanceof Error ? err.message : 'Inconnue'}`);
    } finally {
      setLlmLoading(false);
    }
  };

  // ─── VUE DÉTAIL ───────────────────────────────────────────────────────────

  if (selectedDoc) {
    const catCfg = CATEGORY_CONFIG[selectedDoc.category];
    const secCfg = SECTOR_CONFIG[selectedDoc.sector];

    return (
      <div>
        {/* Barre de navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <button
            onClick={() => setSelectedDoc(null)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
              background: '#f1f5f9', border: 'none', borderRadius: '10px',
              cursor: 'pointer', fontSize: '0.85rem', color: '#475569', fontWeight: '600'
            }}
          >
            <ArrowLeft size={16} /> Retour
          </button>
          <div style={{ flex: 1 }} />
          {selectedDoc.file_path && (
            <button
              onClick={() => downloadPdf(selectedDoc)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
                background: '#ecfdf5', border: '1px solid #86efac', borderRadius: '10px',
                cursor: 'pointer', fontSize: '0.85rem', color: '#059669', fontWeight: '600'
              }}
            >
              <Download size={14} /> Télécharger PDF
            </button>
          )}
          <button
            onClick={() => openEditForm(selectedDoc)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
              background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px',
              cursor: 'pointer', fontSize: '0.85rem', color: '#2563eb', fontWeight: '600'
            }}
          >
            <Edit3 size={14} /> Modifier
          </button>
          <button
            onClick={() => handleDelete(selectedDoc)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
              background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px',
              cursor: 'pointer', fontSize: '0.85rem', color: '#dc2626', fontWeight: '600'
            }}
          >
            <Trash2 size={14} /> Supprimer
          </button>
        </div>

        {/* En-tête */}
        <div style={{
          background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0',
          padding: '24px', marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <span style={{
              padding: '4px 12px', borderRadius: '8px', fontSize: '0.78rem', fontWeight: '600',
              background: catCfg.bg, color: catCfg.color
            }}>
              {catCfg.label}
            </span>
            <span style={{
              padding: '4px 12px', borderRadius: '8px', fontSize: '0.78rem', fontWeight: '600',
              background: secCfg.bg, color: secCfg.color
            }}>
              {secCfg.label}
            </span>
            {selectedDoc.chunk_count > 0 && (
              <span style={{
                padding: '4px 12px', borderRadius: '8px', fontSize: '0.78rem', fontWeight: '600',
                background: '#ecfdf5', color: '#059669'
              }}>
                <Check size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
                {selectedDoc.chunk_count} segments indexés
              </span>
            )}
          </div>
          <h2 style={{ margin: '0 0 8px', color: '#1e293b', fontSize: '1.3rem' }}>
            {selectedDoc.title}
          </h2>
          {selectedDoc.summary && (
            <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>{selectedDoc.summary}</p>
          )}
          {selectedDoc.source_url && (
            <a
              href={selectedDoc.source_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '10px',
                color: '#2563eb', fontSize: '0.85rem', textDecoration: 'none'
              }}
            >
              <ExternalLink size={14} /> Source officielle
            </a>
          )}
        </div>

        {/* Contenu */}
        <div style={{
          background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0',
          padding: '24px', lineHeight: '1.7', fontSize: '0.95rem', color: '#334155'
        }}>
          {loadingContent ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#64748b' }}>
              <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
              Chargement du contenu...
            </div>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: renderMarkdown(docContent) }} />
          )}
        </div>
      </div>
    );
  }

  // ─── VUE GRILLE (DÉFAUT) ──────────────────────────────────────────────────

  return (
    <div>
      {/* BARRE D'ACTIONS */}
      <div style={{
        display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center'
      }}>
        {/* Recherche */}
        <div style={{
          flex: '1 1 200px', display: 'flex', alignItems: 'center', gap: '8px',
          background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '8px 14px'
        }}>
          <Search size={16} color="#94a3b8" />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{
              border: 'none', outline: 'none', flex: 1, fontSize: '0.9rem',
              background: 'transparent', color: '#334155'
            }}
          />
        </div>

        {/* Filtre secteur */}
        <select
          value={filterSector}
          onChange={e => setFilterSector(e.target.value as RegulatorySector | 'all')}
          style={{
            padding: '8px 14px', borderRadius: '10px', border: '1px solid #e2e8f0',
            fontSize: '0.85rem', color: '#475569', cursor: 'pointer', background: 'white'
          }}
        >
          <option value="all">Tous secteurs</option>
          <option value="tertiaire">Tertiaire</option>
          <option value="industriel">Industriel</option>
        </select>

        {/* Filtre catégorie */}
        <select
          value={filterCategory}
          onChange={e => setFilterCategory(e.target.value as RegulatoryCategory | 'all')}
          style={{
            padding: '8px 14px', borderRadius: '10px', border: '1px solid #e2e8f0',
            fontSize: '0.85rem', color: '#475569', cursor: 'pointer', background: 'white'
          }}
        >
          <option value="all">Toutes catégories</option>
          {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>

        {/* Bouton poser une question */}
        <button
          onClick={() => setShowQuestion(!showQuestion)}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
            background: showQuestion ? '#059669' : 'white',
            color: showQuestion ? 'white' : '#059669',
            border: `1px solid ${showQuestion ? '#059669' : '#86efac'}`,
            borderRadius: '10px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600',
            transition: 'all 0.2s'
          }}
        >
          <MessageSquare size={16} /> Poser une question
        </button>

        {/* Bouton ajouter */}
        <button
          onClick={openCreateForm}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
            background: '#059669', color: 'white', border: 'none',
            borderRadius: '10px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600'
          }}
        >
          <Plus size={16} /> Ajouter
        </button>
      </div>

      {/* PANNEAU QUESTION LLM */}
      {showQuestion && (
        <div style={{
          background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0',
          padding: '20px', marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <HelpCircle size={18} color="#059669" />
            <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.95rem' }}>
              Interroger la base réglementaire
            </span>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              placeholder="Ex : Quelles sont les obligations de vérification périodique pour les ERP ?"
              value={question}
              onChange={e => setQuestion(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && askQuestion()}
              style={{
                flex: 1, padding: '10px 16px', borderRadius: '10px',
                border: '1px solid #e2e8f0', fontSize: '0.9rem', outline: 'none'
              }}
            />
            <button
              onClick={askQuestion}
              disabled={llmLoading || !question.trim()}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px',
                background: llmLoading ? '#94a3b8' : '#059669', color: 'white', border: 'none',
                borderRadius: '10px', cursor: llmLoading ? 'not-allowed' : 'pointer',
                fontSize: '0.85rem', fontWeight: '600'
              }}
            >
              {llmLoading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
              {llmLoading ? 'Analyse...' : 'Envoyer'}
            </button>
          </div>

          {/* Réponse LLM */}
          {llmAnswer && (
            <div style={{
              marginTop: '16px', padding: '16px', background: '#f8fafc',
              borderRadius: '12px', border: '1px solid #e2e8f0'
            }}>
              <div
                style={{ lineHeight: '1.7', fontSize: '0.92rem', color: '#334155' }}
                dangerouslySetInnerHTML={{ __html: renderMarkdown(llmAnswer) }}
              />
              {llmSources.length > 0 && (
                <div style={{
                  marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0'
                }}>
                  <span style={{ fontWeight: '700', fontSize: '0.82rem', color: '#475569' }}>
                    Sources consultées :
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                    {llmSources.map((src, idx) => (
                      <span
                        key={idx}
                        onClick={() => {
                          const doc = documents.find(d => d.id === src.document_id);
                          if (doc) viewDocument(doc);
                        }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          padding: '4px 10px', background: '#eff6ff', borderRadius: '6px',
                          fontSize: '0.78rem', color: '#2563eb', cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        <FileText size={12} />
                        {src.title}
                        {src.pages.length > 0 && ` (p.${src.pages.join(',')})`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ÉTAT DE PROGRESSION (upload/indexation) */}
      {uploadProgress && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px',
          background: uploadProgress.startsWith('✓') ? '#ecfdf5' : uploadProgress.startsWith('Erreur') ? '#fef2f2' : '#fffbeb',
          border: `1px solid ${uploadProgress.startsWith('✓') ? '#86efac' : uploadProgress.startsWith('Erreur') ? '#fecaca' : '#fde68a'}`,
          borderRadius: '10px', marginBottom: '16px', fontSize: '0.85rem',
          color: uploadProgress.startsWith('✓') ? '#059669' : uploadProgress.startsWith('Erreur') ? '#dc2626' : '#92400e'
        }}>
          {uploadProgress.startsWith('✓')
            ? <Check size={16} />
            : <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
          {uploadProgress}
        </div>
      )}

      {/* GRILLE DE DOCUMENTS */}
      {loading ? (
        <div style={{
          textAlign: 'center', padding: '60px 0', color: '#94a3b8',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px'
        }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ margin: 0, fontSize: '0.9rem' }}>Chargement des documents...</p>
        </div>
      ) : filteredDocs.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 0', color: '#94a3b8',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px'
        }}>
          <BookOpen size={40} strokeWidth={1.2} />
          <p style={{ margin: 0, fontSize: '0.95rem' }}>
            {documents.length === 0 ? 'Aucun document réglementaire.' : 'Aucun résultat pour ces filtres.'}
          </p>
          {documents.length === 0 && (
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#cbd5e1' }}>
              Cliquez sur "Ajouter" pour importer un document PDF.
            </p>
          )}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '16px'
        }}>
          {filteredDocs.map(doc => {
            const catCfg = CATEGORY_CONFIG[doc.category];
            const secCfg = SECTOR_CONFIG[doc.sector];
            const CatIcon = catCfg.icon;

            return (
              <div
                key={doc.id}
                onClick={() => viewDocument(doc)}
                style={{
                  background: 'white', padding: '20px', borderRadius: '16px',
                  border: '1px solid #e2e8f0', cursor: 'pointer',
                  transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                  display: 'flex', flexDirection: 'column', gap: '10px'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = catCfg.color;
                  e.currentTarget.style.boxShadow = `0 4px 12px ${catCfg.color}20`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)';
                }}
              >
                {/* Icône + Badges */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    background: catCfg.bg, padding: '8px', borderRadius: '10px', color: catCfg.color
                  }}>
                    <CatIcon size={18} />
                  </div>
                  <span style={{
                    padding: '3px 8px', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '600',
                    background: secCfg.bg, color: secCfg.color
                  }}>
                    {secCfg.label}
                  </span>
                  {doc.file_path && (
                    <span title="PDF disponible"><FileText size={14} color="#94a3b8" /></span>
                  )}
                  <div style={{ flex: 1 }} />
                  {doc.is_processed && doc.chunk_count > 0 ? (
                    <span style={{
                      fontSize: '0.7rem', color: '#059669', fontWeight: '600',
                      display: 'flex', alignItems: 'center', gap: '3px'
                    }}>
                      <Check size={12} /> {doc.chunk_count}
                    </span>
                  ) : doc.file_path ? (
                    <span style={{ fontSize: '0.7rem', color: '#d97706', fontWeight: '600' }}>
                      Non indexé
                    </span>
                  ) : null}
                </div>

                {/* Titre */}
                <h3 style={{ margin: 0, color: '#1e293b', fontSize: '0.95rem', fontWeight: '700' }}>
                  {doc.title}
                </h3>

                {/* Résumé */}
                {doc.summary && (
                  <p style={{
                    margin: 0, color: '#64748b', fontSize: '0.82rem', lineHeight: '1.5',
                    overflow: 'hidden', display: '-webkit-box',
                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                  }}>
                    {doc.summary}
                  </p>
                )}

                {/* Actions rapides */}
                <div
                  style={{ display: 'flex', gap: '6px', marginTop: 'auto', paddingTop: '4px' }}
                  onClick={e => e.stopPropagation()}
                >
                  {doc.file_path && (
                    <button
                      onClick={() => downloadPdf(doc)}
                      title="Télécharger le PDF"
                      style={{
                        padding: '4px 8px', background: '#f1f5f9', border: 'none',
                        borderRadius: '6px', cursor: 'pointer', color: '#475569'
                      }}
                    >
                      <Download size={13} />
                    </button>
                  )}
                  {doc.file_path && !doc.is_processed && (
                    <button
                      onClick={() => reindexDocument(doc)}
                      title="Indexer ce document"
                      style={{
                        padding: '4px 8px', background: '#fffbeb', border: 'none',
                        borderRadius: '6px', cursor: 'pointer', color: '#d97706',
                        fontSize: '0.72rem', fontWeight: '600'
                      }}
                    >
                      Indexer
                    </button>
                  )}
                  <div style={{ flex: 1 }} />
                  <button
                    onClick={() => openEditForm(doc)}
                    title="Modifier"
                    style={{
                      padding: '4px 8px', background: '#eff6ff', border: 'none',
                      borderRadius: '6px', cursor: 'pointer', color: '#2563eb'
                    }}
                  >
                    <Edit3 size={13} />
                  </button>
                  <button
                    onClick={() => handleDelete(doc)}
                    title="Supprimer"
                    style={{
                      padding: '4px 8px', background: '#fef2f2', border: 'none',
                      borderRadius: '6px', cursor: 'pointer', color: '#dc2626'
                    }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL CRUD */}
      {showForm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '20px', padding: '32px',
            maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '1.2rem', color: '#1e293b' }}>
                {editingDoc ? 'Modifier le document' : 'Ajouter un document'}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Titre */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontWeight: '600', color: '#475569', fontSize: '0.85rem', display: 'block', marginBottom: '6px' }}>
                Titre *
              </label>
              <input
                type="text"
                value={formTitle}
                onChange={e => setFormTitle(e.target.value)}
                placeholder="Ex : NF C 15-100 — Installations BT"
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: '10px',
                  border: '1px solid #e2e8f0', fontSize: '0.9rem', outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Catégorie + Secteur */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontWeight: '600', color: '#475569', fontSize: '0.85rem', display: 'block', marginBottom: '6px' }}>
                  Catégorie
                </label>
                <select
                  value={formCategory}
                  onChange={e => setFormCategory(e.target.value as RegulatoryCategory)}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: '10px',
                    border: '1px solid #e2e8f0', fontSize: '0.9rem', cursor: 'pointer',
                    boxSizing: 'border-box'
                  }}
                >
                  {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                    <option key={key} value={key}>{cfg.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontWeight: '600', color: '#475569', fontSize: '0.85rem', display: 'block', marginBottom: '6px' }}>
                  Secteur
                </label>
                <select
                  value={formSector}
                  onChange={e => setFormSector(e.target.value as RegulatorySector)}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: '10px',
                    border: '1px solid #e2e8f0', fontSize: '0.9rem', cursor: 'pointer',
                    boxSizing: 'border-box'
                  }}
                >
                  {Object.entries(SECTOR_CONFIG).map(([key, cfg]) => (
                    <option key={key} value={key}>{cfg.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Résumé */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontWeight: '600', color: '#475569', fontSize: '0.85rem', display: 'block', marginBottom: '6px' }}>
                Résumé
              </label>
              <textarea
                value={formSummary}
                onChange={e => setFormSummary(e.target.value)}
                placeholder="Description courte du document..."
                rows={3}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: '10px',
                  border: '1px solid #e2e8f0', fontSize: '0.9rem', outline: 'none',
                  resize: 'vertical', boxSizing: 'border-box'
                }}
              />
            </div>

            {/* URL source */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontWeight: '600', color: '#475569', fontSize: '0.85rem', display: 'block', marginBottom: '6px' }}>
                URL source officielle
              </label>
              <input
                type="url"
                value={formSourceUrl}
                onChange={e => setFormSourceUrl(e.target.value)}
                placeholder="https://www.legifrance.gouv.fr/..."
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: '10px',
                  border: '1px solid #e2e8f0', fontSize: '0.9rem', outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Upload PDF */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ fontWeight: '600', color: '#475569', fontSize: '0.85rem', display: 'block', marginBottom: '6px' }}>
                Document PDF {editingDoc?.file_path ? '(remplacer)' : ''}
              </label>
              <div
                onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#059669'; }}
                onDragLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
                onDrop={e => {
                  e.preventDefault();
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  const file = e.dataTransfer.files[0];
                  if (file?.type === 'application/pdf') setFormFile(file);
                }}
                style={{
                  border: '2px dashed #e2e8f0', borderRadius: '12px', padding: '24px',
                  textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s'
                }}
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = '.pdf';
                  input.onchange = (e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) setFormFile(file);
                  };
                  input.click();
                }}
              >
                {formFile ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#059669' }}>
                    <FileText size={20} />
                    <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>{formFile.name}</span>
                    <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                      ({(formFile.size / 1024 / 1024).toFixed(1)} Mo)
                    </span>
                  </div>
                ) : (
                  <div style={{ color: '#94a3b8' }}>
                    <Upload size={24} style={{ marginBottom: '8px' }} />
                    <p style={{ margin: 0, fontSize: '0.85rem' }}>
                      Glissez un PDF ici ou cliquez pour sélectionner
                    </p>
                  </div>
                )}
              </div>
              {editingDoc?.file_name && !formFile && (
                <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#64748b' }}>
                  Fichier actuel : {editingDoc.file_name}
                </p>
              )}
            </div>

            {/* Progression */}
            {uploadProgress && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px',
                background: uploadProgress.startsWith('✓') ? '#ecfdf5' : uploadProgress.startsWith('Erreur') ? '#fef2f2' : '#fffbeb',
                borderRadius: '10px', marginBottom: '16px', fontSize: '0.85rem',
                color: uploadProgress.startsWith('✓') ? '#059669' : uploadProgress.startsWith('Erreur') ? '#dc2626' : '#92400e'
              }}>
                {uploadProgress.startsWith('✓')
                  ? <Check size={16} />
                  : <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                {uploadProgress}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowForm(false)}
                style={{
                  padding: '10px 20px', background: '#f1f5f9', border: 'none',
                  borderRadius: '10px', cursor: 'pointer', fontSize: '0.85rem',
                  color: '#475569', fontWeight: '600'
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formTitle.trim()}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '10px 20px', background: saving ? '#94a3b8' : '#059669',
                  color: 'white', border: 'none', borderRadius: '10px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem', fontWeight: '600'
                }}
              >
                {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS spin (au cas où le parent ne le charge pas) */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
